import type { LocalizedStringFormatter } from 'react-aria';

/**
 * Represents a localized piece of text used within a component.
 * It can either be a plain string, or a function that returns a plain string based on provided context.
 */
export type LocalizedString =
  | string
  | ((
      args?: Record<string, string | number | boolean>,
      formatter?: LocalizedStringFormatter<string, LocalizedString>,
    ) => string);

/** A collection of localized strings used by Cimpress UI components. */
export type LocalizedMessages = {
  common: {
    /** An ARIA label for a button that clears the value of a field. */
    clearValue: LocalizedString;
    /** An ARIA label for a button that closes or dismisses an alert, a callout, a dialog, or other overlay component. Used for accessibility purposes to clearly indicate the button's function to screen reader users. */
    dismiss: LocalizedString;
    /** A visually hidden label that indicates a text field can be cleared by pressing the Escape key. This text is only announced by screen readers. */
    clearValueDescription: LocalizedString;
  };
  copyToClipboard: {
    /** The message displayed in a tooltip when a value is successfully copied to clipboard. */
    success: LocalizedString;
    /** The message displayed in a tooltip when a value fails to be copied to clipboard. */
    failure: LocalizedString;
  };
  label: {
    /** A visually hidden label that indicates a form field is required. This text is appended to the field's visible label and is only announced by screen readers. */
    required: LocalizedString;
  };
  datePicker: {
    /** An ARIA label for a button that clears the value of a date picker field. */
    clearValue: LocalizedString;
    /** An ARIA label for a button that opens a calendar popup. Part of a date picker. */
    openCalendar: LocalizedString;
    /** An ARIA label for a button that displays the previous month in a calendar popup. Part of a date picker. */
    previousMonth: LocalizedString;
    /** An ARIA label for a button that displays the next month in a calendar popup. Part of a date picker. */
    nextMonth: LocalizedString;
    /** An ARIA label for a button that changes the calendar popup to a single-year view. Part of a date picker. */
    switchToYearView: LocalizedString;
    /** An ARIA label for a button that displays the previous year in a calendar popup. Part of a date picker. */
    previousYear: LocalizedString;
    /** An ARIA label for a button that displays the next year in a calendar popup. Part of a date picker. */
    nextYear: LocalizedString;
    /** An ARIA label for a button that changes the calendar popup to a multi-year view. Part of a date picker. */
    switchToMultiYearView: LocalizedString;
    /** An ARIA label for a button that displays the previous section of a multi-year view in a calendar popup. Year count will always be larger than 1. Part of a date picker. */
    previousMultiYearSection: LocalizedString;
    /** An ARIA label for a button that displays the next section of a multi-year view in a calendar popup. Year count will always be larger than 1. Part of a date picker. */
    nextMultiYearSection: LocalizedString;
  };
  numberField: {
    /** An ARIA label for a button that decrements the value of a number field. */
    decrement: LocalizedString;
    /** An ARIA label for a button that increments the value of a number field. */
    increment: LocalizedString;
  };
  pagination: {
    /** An ARIA label for a button that navigates to a specific page. Part of a pagination navigation structure. */
    goToPage: LocalizedString;
    /** An ARIA label for a button that navigates to the previous page. Part of a pagination navigation structure. */
    previousPage: LocalizedString;
    /** An ARIA label for a button that navigates to the next page. Part of a pagination navigation structure. */
    nextPage: LocalizedString;
    /** A screen reader announcement for when the selected page changes. Part of a pagination navigation structure. */
    selectedPageAnnouncement: LocalizedString;
  };
  slider: {
    /** An ARIA label for a slider thumb that is used to select the start (minimum) value of a range. */
    rangeStart: LocalizedString;
    /** An ARIA label for a slider thumb that is used to select the end (maximum) value of a range. */
    rangeEnd: LocalizedString;
  };
  tagField: {
    /** A label displayed within a tag field to inform the user about the number of selected items. */
    itemsSelected: LocalizedString;
  };
  filePicker: {
    /** A label for the button that opens the native file picker dialog. */
    chooseFile: LocalizedString;
    /** A label for when no files are selected. */
    noFilesSelected: LocalizedString;
    /** A label for when multiple files are selected. */
    multipleFilesSelected: LocalizedString;
  };
  table: {
    /** An ARIA label for a spinner displayed when data is being loaded into a table. */
    loadingData: LocalizedString;
    /** An ARIA label for a button that toggles the sort order of a specific column in a data table. This label will be followed by the name of the column. */
    toggleColumnSortOrder: LocalizedString;
  };
  simpleAuthTool: {
    /** A label used for a button that redirects to a login flow. */
    logIn: LocalizedString;
    /** A label used for a button that logs the current user out. */
    logOut: LocalizedString;
    /** An ARIA label for a popover that contains information about the logged in user. */
    profileDetails: LocalizedString;
    /** A name that's displayed when the current user's name is unknown. */
    unknownUser: LocalizedString;
  };
  UNSTABLE_collection: {
    /** An ARIA label for a spinner displayed when the list of items is first being loaded in a collection. */
    loading: LocalizedString;
    /** An ARIA label for a spinner displayed when more items are being loaded into a collection. */
    loadingMore: LocalizedString;
    /** A label displayed within a select to inform the user about the number of selected items. */
    itemsSelected: LocalizedString;
  };
};
